import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { normalizeImageUrl } from '../utils/imageUtils';
import RecruitmentService from '../services/RecruitmentService';

const RecruitmentPage = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const isFetchingRef = useRef(false);
    const lastFetchedRef = useRef(false);

    useEffect(() => {
        const fetchJobs = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedRef.current) return;

            isFetchingRef.current = true;
            try {
                const data = await RecruitmentService.getAllJobs();
                const publishedJobs = Array.isArray(data) ? data : [];
                setJobs(publishedJobs);
                lastFetchedRef.current = true;
            } catch (error) {
                console.error("Lỗi tải danh sách tuyển dụng:", error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchJobs();
    }, []);

    return (
        <main className="recruitment-page" style={{ borderBottom: '1px solid #ececec', paddingBottom: 50 }}>
            {/* ════ MINIMAL BREADCRUMBS ════ */}
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item">
                                <Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link>
                            </li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">
                                Tuyển dụng
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <h1 className="heading-primary text-center" style={{ margin: '60px 0' }}>
                    <span>Cơ hội nghề nghiệp</span>
                </h1>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Đang tải...</span>
                        </div>
                    </div>
                ) : jobs.length > 0 ? (
                    <div className="row g-4 row-cols-lg-3 row-cols-md-2 row-cols-1 mb-4">
                        {jobs.map((job, idx) => (
                            <div className="col" key={job.id || idx}>
                                <div className="card h-100 shadow-sm border-0 overflow-hidden job-card-hover" style={{ transition: 'all 0.3s ease', borderRadius: 15 }}>
                                    <Link
                                        to={`/tuyen-dung/${job.slug || job.id}`}
                                        title={job.title}
                                        className="d-block overflow-hidden"
                                        style={{ height: '250px', position: 'relative' }}
                                    >
                                        <img
                                            src={normalizeImageUrl(job.imageUrl)}
                                            alt={job.title}
                                            loading="lazy"
                                            className="transition-scale h-100 w-100"
                                            style={{ transition: '0.5s', objectFit: 'cover', minHeight: '100%' }}
                                        />
                                        <div className="position-absolute top-0 start-0 m-3">
                                            <span className="badge bg-primary rounded-pill px-3 py-2 shadow-sm">
                                                {job.salary || 'Thỏa thuận'}
                                            </span>
                                        </div>
                                    </Link>
                                    <div className="card-body p-4 d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-primary fw-bold" style={{ fontSize: 13 }}>{job.department || 'Tuyển dụng'}</span>
                                            <span className="text-muted" style={{ fontSize: 12 }}>
                                                <i className="fa fa-map-marker me-1"></i> {job.location || 'Hà Nội'}
                                            </span>
                                        </div>
                                        <h3 className="card-title h5 mb-3">
                                            <Link to={`/tuyen-dung/${job.slug || job.id}`} style={{ color: '#333', textDecoration: 'none', fontWeight: 700, lineHeight: 1.4 }}>
                                                {job.title}
                                            </Link>
                                        </h3>
                                        <p className="card-text text-muted flex-grow-1 mb-4" style={{ fontSize: 13, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: '1.6' }}>
                                            {(() => {
                                                const rawText = (job.description || job.content || '')
                                                    .replace(/<[^>]*>?/gm, '')
                                                    .replace(/&nbsp;/g, ' ')
                                                    .replace(/&amp;/g, '&')
                                                    .replace(/&lt;/g, '<')
                                                    .replace(/&gt;/g, '>')
                                                    .replace(/&quot;/g, '"')
                                                    .replace(/&#39;/g, "'")
                                                    .trim();

                                                // If the content starts with the title, skip it to avoid redundancy
                                                if (rawText.toLowerCase().startsWith(job.title.toLowerCase())) {
                                                    return rawText.substring(job.title.length).trim().replace(/^[^\w]+/, '');
                                                }
                                                return rawText;
                                            })()}
                                        </p>

                                        <div className="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3" style={{ fontSize: 13 }}>
                                            <span className="text-muted"><i className="fa fa-clock-o me-1"></i> {job.type || 'Toàn thời gian'}</span>
                                            <span className="text-danger fw-medium">
                                                Hạn: {job.deadline ? new Date(job.deadline).toLocaleDateString('vi-VN') : 'Càng sớm càng tốt'}
                                            </span>
                                        </div>

                                        <Link to={`/tuyen-dung/${job.slug || job.id}`} className="btn btn-main rounded-pill w-100 py-2">
                                            Chi tiết công việc
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div style={{ maxWidth: 400, margin: '0 auto' }}>
                            <img src="/assets/images/no-jobs.png" alt="No jobs" style={{ width: '100%', marginBottom: 20, opacity: 0.5 }} />
                            <p className="text-muted">Hiện tại chúng tôi chưa có vị trí tuyển dụng mới. Vui lòng quay lại sau.</p>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
};

export default RecruitmentPage;



